import React from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import {
  customError,
  dimGrey,
  lightBackground1,
  lightBlack,
  outlineSeparator,
} from '../../../../../mainTheme';

interface InsightsPanelProps {
  title: string;
  subtitle?: string | null;
  action?: React.ReactNode;
  // Fetch in flight: an inline spinner replaces the children. Inline rather
  // than the full-page LoadingSpinner so the rest of the tab stays usable.
  loading?: boolean;
  // When set, shown in place of the children (same treatment as the
  // CallStats ChartPanel) so a failed fetch never reads as "no data".
  error?: string | null;
  children: React.ReactNode;
  testId?: string;
}

// Card shell for one block of the patient Insights tab (chart, table). Same
// border/radius as the Overview cards so the tab reads as one surface.
const InsightsPanel: React.FC<InsightsPanelProps> = ({
  title,
  subtitle,
  action,
  loading,
  error,
  children,
  testId,
}) => (
  <Paper
    elevation={0}
    data-testid={testId}
    sx={{
      p: '20px 24px',
      borderRadius: '8px',
      border: `1px solid ${outlineSeparator}`,
      backgroundColor: lightBackground1,
    }}
  >
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
      mb={2}
    >
      <Box>
        <Typography
          sx={{
            color: lightBlack,
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: '0.2px',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              color: dimGrey,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {loading ? (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={32} aria-label={`Loading ${title}`} />
      </Box>
    ) : error ? (
      <Typography
        role="alert"
        sx={{
          color: customError,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          py: 3,
          textAlign: 'center',
        }}
      >
        {error}
      </Typography>
    ) : (
      children
    )}
  </Paper>
);

export default InsightsPanel;
